import cx from "classnames"
import { nanoid } from "nanoid"
import type React from "react"
import { Children, cloneElement, isValidElement } from "react"

type LayoutStackPropsType = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode
  isTable?: boolean
  isPageHeader?: boolean
}

type LayoutStackItemPropsType =
  | (React.HTMLAttributes<HTMLDivElement> & {
      children: React.ReactNode
      isTable: never
      isTableHeader: never
    })
  | (React.HTMLAttributes<HTMLTableRowElement> & {
      children: React.ReactNode
      isTable?: boolean
      isTableHeader?: boolean
    })

type LayoutStackLinkItemPropsType =
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    children: React.ReactNode
  }

const LayoutStackItem = ({
  children,
  isTable,
  className,
  isTableHeader: _isTableHeader,
  ...props
}: LayoutStackItemPropsType) => {
  const Comp = isTable ? "tr" : "div"
  return (
    <Comp className={cx("cobalt-layout-stack__item", className)} {...props}>
      {children}
    </Comp>
  )
}

const LayoutStackLinkItem = ({
  children,
  className,
  ...hyperlinkProps
}: LayoutStackLinkItemPropsType) => {
  return (
    <a
      className={cx("cobalt-layout-stack__item", className)}
      {...hyperlinkProps}
    >
      {children}
    </a>
  )
}

const isStackItem = (
  child: React.ReactNode,
): child is React.ReactElement<Omit<LayoutStackItemPropsType, "children">> => {
  return (
    isValidElement(child) &&
    (child.type === LayoutStackItem || child.type === LayoutStackLinkItem)
  )
}

const LayoutStack = ({
  children,
  isTable,
  isPageHeader,
  className,
  ...props
}: LayoutStackPropsType) => {
  const classNames = cx(
    "cobalt-layout-stack",
    {
      "cobalt-layout--isPageHeader": isPageHeader,
    },
    className,
  )
  if (isTable) {
    const headerElements: React.ReactElement[] = []
    const bodyElements: React.ReactElement[] = []
    Children.map(children, (child) => {
      if (isStackItem(child)) {
        const newChild = cloneElement(child, { isTable: true, key: nanoid() })
        if (newChild.props.isTableHeader) {
          headerElements.push(newChild)
        } else {
          bodyElements.push(newChild)
        }
      }
      return child
    })
    return (
      <table className={classNames} {...props}>
        {!!headerElements.length && <thead>{headerElements}</thead>}
        <tbody>{bodyElements}</tbody>
      </table>
    )
  } else {
    return (
      <div className={classNames} {...props}>
        {children}
      </div>
    )
  }
}

LayoutStack.Item = LayoutStackItem

LayoutStack.LinkItem = LayoutStackLinkItem

export default LayoutStack
